import { Alert, Button, Form } from "antd";
import {
  filter,
  forEach,
  isEmpty,
  isNull,
  isUndefined,
  mapValues,
  split,
} from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields, parseSelectOptions } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";
import { parseClusterSelectOption } from "../twistlock/TwistLockMappingFormV2";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { parseRepoVersionString,
  IMAGE_VALUE,
  parseVersionSelectOptions,
  buildRepoVersionString,
  getNamesFromMap,
  HOST_VALUE,
  APPLICATION_SECURITY_VALUE,
  API_SECURITY_VALUE,
  PRISMA_CLOUD_COMPUTE_RADIO_OPTIONS,
  IMAGE_TYPE_OPTIONS,
  buildFlattenedVersions,
 }
from "./PrismaCloudComputeMappingUtils";

const PrismaCloudComputeMappingForm = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const selectedNames = useRef({});
  const featureFlags = useFeatureFlag();
  const isVersionMappingEnabled = featureFlags.PRISMA_COMPUTE_ENABLE_IMAGE_VERSION_MAPPING;
  
  const initialFormValues = useMemo(() => ({
    product: undefined,
    subProduct: undefined,
    environment: undefined,
    type: "images",
    imageList: [
      {
        imageType: "deployed",
        deployedList: [{ clusterName: undefined, imageId: undefined, versions: isVersionMappingEnabled ? [] : undefined }],
      },
    ],
    isApiSecurityConfig: undefined,
  }), [isVersionMappingEnabled]);

  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });

  const fetchRepos = useCallback(
    (type, params) => {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          type,
          ...params,
        });
      promise.then((data) => {
        return data.projects;
      });
      return { promise, cancel };
    },
    [props.toolName, props.loginConfigId],
  );

  const projectListRender = useCallback(
    (listField) => {
      const values = form.getFieldsValue().configurations || [];
      const mappingType = values[listField.name]?.type;

      // IMAGE case
      

      const TYPE_RENDERERS = {
        [IMAGE_VALUE]: IMAGE_RENDERER,
        [`${IMAGE_VALUE}_props`]: {
          form,
          listField,
          selectedNames,
          fetchRepos,
        },
        [HOST_VALUE]: HostRenderer,
        [`${HOST_VALUE}_props`]: {
          form,
          listField,
          selectedNames,
          fetchRepos,
        },
        [APPLICATION_SECURITY_VALUE]: AppSecRenderer,
        [`${APPLICATION_SECURITY_VALUE}_props`]: {
          listField,
          selectedNames,
          fetchRepos,
        },
        [API_SECURITY_VALUE]: ApiSecurityRenderer,
        [`${API_SECURITY_VALUE}_props`]: { listField },
      };

      const Renderer = TYPE_RENDERERS[mappingType] || (() => null);
      const props = TYPE_RENDERERS[`${mappingType}_props`] || {}
      return <Renderer {...props} />;
    },
    [form, fetchRepos],
  );

  const extraFields = useMemo(
    () => [
      {
        name: "type",
        type: FieldTypes.RADIO,
        options: PRISMA_CLOUD_COMPUTE_RADIO_OPTIONS,
      },
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender,
      },
    ],
    [projectListRender],
  );

  const getExtraFieldsData = useCallback((config, index) => {
    const { type, imageList, hostId, allHosts, hostClusterName, codeRepoId, isApiSecurityConfig } =
      config;
    const names = selectedNames.current[index] || {};

    switch (type) {
      case IMAGE_VALUE: {
        if(!isVersionMappingEnabled) {
          const clusterMap = {};
          let registryIds = [];
          let ciIds = [];

          forEach(imageList, (block) => {
            const { imageType } = block;

            if (imageType === "deployed") {
              (block.deployedList || []).forEach(
                ({ clusterName, imageId = [] }) => {
                  const key =
                    isEmpty(clusterName) && isEmpty(imageId)
                      ? "all"
                      : clusterName
                        ? clusterName
                        : "";
                  clusterMap[key] = (clusterMap[key] || []).concat(imageId);
                },
              );
            }
            if (imageType === "registry") {
              registryIds = registryIds.concat(block.registryId || "all");
            }
            if (imageType === "ci") {
              ciIds = ciIds.concat(block.ciId || "all");
            }
          });

          const registryNames =
            names.registry && names.registry.length
              ? names.registry.join(",")
              : registryIds.join(",") || "";

          const ciNames =
            names.ci && names.ci.length
              ? names.ci.join(",")
              : ciIds.join(",") || "";

          const clusterWithImages = Object.entries(clusterMap)
            .map(([cluster, imgs]) => {
              return cluster === "all" && isEmpty(imgs)
                ? "all"
                : `${cluster}|:${imgs.join(";")}`;
            })
            .join(",");

          return {
            type,
            clusterWithImages,
            registryId: registryIds.length ? registryIds.join(",") : "",
            registryName: registryNames || "",
            ciImageId: ciIds.length ? ciIds.join(",") : "",
            ciImageName: ciNames || "",
          };
        } else {
          const deployedClusterMap = {};
          const deployedRepoMap = {};
          const registryRepoMap = {};
          const ciRepoMap = {};

          let oldRegistryIds = [];
          const oldCiIds = [];
          
          // Version trackers
          let hasDeployedVersions = false;
          let hasRegistryVersions = false;
          let hasCiVersions = false;
  
          forEach(imageList, (block) => {
            const { imageType } = block;
            if (imageType === "deployed") {
              (block.deployedList || []).forEach(
                ({ clusterName, imageId, versions = [] }) => {
                  if (versions.length > 0) {
                    hasDeployedVersions = true;
                  }
                  const key = isEmpty(clusterName) ? "" : clusterName;
                  if (isEmpty(clusterName) && isEmpty(imageId)) {
                    deployedClusterMap["all"] = [];
                  } else if (imageId) {
                    deployedClusterMap[key] = (deployedClusterMap[key] || []).concat(imageId);
                    deployedRepoMap[imageId] = (deployedRepoMap[imageId] || []).concat(versions);
                  } else if (!isEmpty(clusterName) && isEmpty(imageId)) {
                     deployedClusterMap[key] = (deployedClusterMap[key] || []);
                  }
                },
              );
            }
            if (imageType === "registry") {
              const versions = block.versions || [];
              if (versions.length > 0) {
                hasRegistryVersions = true;
              }
              // Always populate both old and new data structures
              oldRegistryIds = oldRegistryIds.concat(block.registryId || "all");
              if (block.registryId) {
                const repo = block.registryId; // singular
                if (versions.length > 0) {
                   registryRepoMap[repo] = (registryRepoMap[repo] || []).concat(versions);
                } else {
                   registryRepoMap[repo] = (registryRepoMap[repo] || []);
                }
              } else {
                 registryRepoMap["all"] = [];
              }
            }
            if (imageType === "ci") {
              const versions = block.versions || [];
              if (versions.length > 0) {
                hasCiVersions = true;
              }
              const repo = block.ciId; 
              if (repo) {
                oldCiIds.push(repo);
                if (versions.length > 0) {
                  ciRepoMap[repo] = (ciRepoMap[repo] || []).concat(versions);
                } else {
                  ciRepoMap[repo] = (ciRepoMap[repo] || []);
                }
              } else {
                oldCiIds.push("all");
                ciRepoMap["all"] = [];
              } 
            }
          });
          
          const finalClusterWithImages = Object.entries(deployedClusterMap)
            .map(([cluster, repos]) => {
              if (cluster === "all") return "all";
              return `${cluster}|:${[...new Set(repos)].join(";")}`;
            })
            .join(",");

          const payload = {
            type,
            clusterWithImages: finalClusterWithImages,
          };
          
         // --- Conditionally add payload keys ---

          // Deployed
          if (hasDeployedVersions) {
            payload.deployedImagesWithVersions = buildRepoVersionString(deployedRepoMap);
            payload.deployedVersionsWithIds = buildFlattenedVersions(deployedRepoMap);
          }
          
          // Registry
          if (hasRegistryVersions) {
            payload.registryImagesWithVersions = buildRepoVersionString(registryRepoMap);
            payload.registryVersionsWithIds = buildFlattenedVersions(registryRepoMap);
          };

          const finalRegistryIds = [...new Set(oldRegistryIds)];
          payload.registryId = finalRegistryIds.length ? finalRegistryIds.join(",") : "";
          payload.registryName = getNamesFromMap(finalRegistryIds, names.registryNameMap);

          // CI
          if (hasCiVersions) {
            payload.ciImagesWithVersions = buildRepoVersionString(ciRepoMap);
            payload.ciImageVersionsWithIds = buildFlattenedVersions(ciRepoMap);
          }

          const finalCiIds = [...new Set(oldCiIds)];
          payload.ciImageId = finalCiIds.length ? finalCiIds.join(",") : "";
          payload.ciImageName = getNamesFromMap(finalCiIds, names.ciNameMap);;
          
          return payload;
        }
      }
      case HOST_VALUE: {
        const hostNames = names.hostNames?.length
          ? names.hostNames.join(",")
          : hostId?.join(",") || "ALL-HOSTS";
        const finalHostClusterName =
          hostClusterName === NOT_MAPPED ? undefined : hostClusterName || "ALL-CLUSTERS";
        return {
          type,
          hostClusterName: finalHostClusterName,
          hostId: allHosts ? "ALL-HOSTS" : hostId?.join() || "ALL-HOSTS",
          hostName: allHosts ? "ALL-HOSTS" : hostNames,
        };
      }
      case APPLICATION_SECURITY_VALUE: {
        const codeRepoNames = names.codeRepo?.join(",") || "All";
        return {
          type,
          codeRepoId: codeRepoId?.join(",") || "All",
          codeRepoName: codeRepoNames,
        };
      }
      case API_SECURITY_VALUE: {
        return {
          type,
          isApiSecurityConfig,
        };
      }
      default:
        return { type };
    }
  }, [isVersionMappingEnabled]);

  const parseExtraFieldsDataFromResponse = useCallback((resp) => {
    const {
      type,
      clusterWithImages = "",
      registryId = "",
      ciImageName = "",
      registryName = "",
      hostId = "",
      hostName,
      codeRepoName,
      codeRepoId = "",
      isApiSecurityConfig,
      registryImagesWithVersions = "",
      ciImagesWithVersions = "",
    } = resp;
      
    //keeping the index as 0 as we show only one configuration on edit
    //as the configurations gets saved individually for multiple mappings too
    if (!selectedNames.current[0]) {
        const createNameMap = (idsStr, namesStr) => {
            const map = {};
            if(!idsStr || !namesStr) return map;
            const ids = idsStr.split(',');
            const names = namesStr.split(',');
            ids.forEach((id, idx) => { if(id && names[idx]) map[id] = names[idx]; });
            return map;
        };

        selectedNames.current[0] = {
            registry: (registryName || "").split(',').filter(Boolean),
            ci: (ciImageName || "").split(',').filter(Boolean),
            hostNames: (hostName || "").split(',').filter(Boolean),
            codeRepo: (codeRepoName || "").split(',').filter(Boolean),
            
            registryNameMap: createNameMap(registryId, registryName),
            ciNameMap: createNameMap(ciImageName, ciImageName),
        };
    }

    if (type === HOST_VALUE) {
      return {
        type,
        ...mapValues(resp, (v, key) =>
          key === "hostClusterName" && isEmpty(v)
            ? NOT_MAPPED
            : v === "ALL-CLUSTERS"
              ? undefined
              : v,
        ),
        hostId:
          !isEmpty(hostId) && hostId !== "ALL-HOSTS" && hostId !== "ALL-HOSTS"
            ? split(hostId, ",")
            : undefined,
        allHosts: hostId === "ALL-HOSTS",
      };
    }

    if (type === APPLICATION_SECURITY_VALUE) {
      return {
        type,
        codeRepoId: !isEmpty(codeRepoId) && codeRepoId !== "All" ? split(codeRepoId, ",") : undefined,
      };
    }

    if (type === API_SECURITY_VALUE) {
      return {
        type,
        isApiSecurityConfig: isApiSecurityConfig === "true"
      };
    }

    const imageList = [];
    if(!isVersionMappingEnabled) {
      if (clusterWithImages.trim() === "all") {
        imageList.push({
          imageType: "deployed",
          deployedList: [{ clusterName: undefined, imageId: [] }],
        });
      } else if (clusterWithImages) {
        const segments = clusterWithImages.split(",");

        // Build one deployed‐block’s deployedList
        const deployedList = [];
        segments.forEach((seg) => {
          if (seg === "all") {
            // “all” → one empty row
            deployedList.push({ clusterName: undefined, imageId: [] });
          } else if (seg.includes("|:")) {
            const [cluster, imagesPart] = seg.split("|:");
            const imgs = imagesPart
              .replace(/;$/, "")
              .split(";")
              .filter((x) => x);

            deployedList.push({
              clusterName:
                cluster === "all" || isEmpty(cluster) ? undefined : cluster,
              imageId: imgs,
            });
          }
        });

        if (deployedList.length) {
          imageList.push({ imageType: "deployed", deployedList });
        }
      }

      // 2) registry
      if (registryId && registryId !== "all") {
        imageList.push({
          imageType: "registry",
          registryId: !isEmpty(registryId) ? registryId.split(",") : undefined,
        });
      } else if (registryId === "all") {
        imageList.push({
          imageType: "registry",
          registryId: undefined,
        });
      }

      // 3) CI
      if (ciImageName && ciImageName !== "all") {
        imageList.push({
          imageType: "ci",
          ciId: !isEmpty(ciImageName) ? ciImageName.split(",") : undefined,
        });
      } else if (ciImageName === "all") {
        imageList.push({
          imageType: "ci",
          ciId: undefined,
        });
      }
    } else {
      const {
        clusterWithImages = "",
        deployedImagesWithVersions = "",
        registryId = "",
        ciImageName = "",
      } = resp;
  
      // 1) Deployed
      const deployedRepoMap = parseRepoVersionString(deployedImagesWithVersions);
      if (clusterWithImages.trim() === "all") {
        imageList.push({
          imageType: "deployed",
          deployedList: [{ clusterName: undefined, imageId: undefined, versions: [] }],
        });
      } else if (clusterWithImages) {
        const deployedList = [];
        clusterWithImages.split(',').forEach(seg => {
            if (seg.includes("|:")) {
                const [cluster, reposPart] = seg.split("|:");
                const clusterName = isEmpty(cluster) ? undefined : cluster;
                const repos = reposPart.split(';').filter(x => x);
                if (repos.length > 0) {
                    repos.forEach(repoId => {
                        deployedList.push({
                            clusterName: clusterName,
                            imageId: repoId,
                            versions: deployedRepoMap[repoId] || [],
                        });
                    });
                } else {
                    deployedList.push({ clusterName: clusterName, imageId: undefined, versions: [] });
                }
            }
        });
        if (deployedList.length > 0) {
          imageList.push({ imageType: "deployed", deployedList });
        }
      }
  
      // 2) registry
      if (resp.registryImagesWithVersions) {
        const registryRepoMap = parseRepoVersionString(registryImagesWithVersions);
        if (registryRepoMap["all"]) {
            imageList.push({ imageType: "registry", registryId: undefined, versions: undefined });
        } else {
            Object.entries(registryRepoMap).forEach(([repo, versions]) => {
              imageList.push({ imageType: "registry", registryId: repo, versions });
            });
        }
      } else {
        if (registryId && registryId !== "all") {
          const ids = registryId.split(",");
          ids.forEach(id => {
             if (id) {
                imageList.push({ 
                    imageType: "registry", 
                    registryId: id, // Populate the ID
                    versions: []    // Empty versions
                });
             }
          });
        } else if (registryId === "all") {
          imageList.push({
            imageType: "registry",
            registryId: undefined,
          });
        }
      }
  
      // 3) CI
      if (resp.ciImagesWithVersions) {
        const ciRepoMap = parseRepoVersionString(ciImagesWithVersions);
        if (ciRepoMap["all"]) {
            imageList.push({ imageType: "ci", ciId: undefined, versions: [] });
        } else {
          Object.entries(ciRepoMap).forEach(([repo, versions]) => {
          imageList.push({ imageType: "ci", ciId: repo, versions: versions });
        });
        }
      } else {
          if (ciImageName && ciImageName !== "all") {
            const ids = ciImageName.split(",");
             ids.forEach(id => {
                if (id) {
                    imageList.push({ 
                        imageType: "ci", 
                        ciId: id, 
                        versions: [] 
                    });
                }
             });
          } else if (ciImageName === "all") {
            imageList.push({
                imageType: "ci",
                ciId: undefined,
            });
          }
      }
    }

    return { type, imageList };
  }, [isVersionMappingEnabled]);

  return (
    <GenericMappingForm
      form={form}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialFormValues[0]}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  );
});

export default PrismaCloudComputeMappingForm;

const ImageBlock = React.memo(
  ({
    form,
    listIdx,
    field,
    remove,
    fetchRepos,
    selectedNames,
    fieldsLength,
  }) => {
    const values =
      form.getFieldsValue().configurations?.[listIdx]?.imageList || [];
    const imgType = values[field.name]?.imageType;

    const fetchRegistryRepo = useCallback((params) => {
      return fetchRepos("registry", params);
    }, [fetchRepos]);
    
    const fetchCICDRepo = useCallback((params) => {
      return fetchRepos("ci", params);
    }, [fetchRepos]);

    const registryFields = useMemo(
      () => [
        {
          name: "registryId",
          colSpan: 12,
          type: FieldTypes.SELECT,
          label: "Image Repository",
          placeholder: "Leave blank to select all",
          props: {
            mode: "multiple",
            api: fetchRegistryRepo,
            onChange: (value, field, selectedOptions) => {
              // store the NAMES (you can pick .label or .name)
              selectedNames.current[listIdx] =
                selectedNames.current[listIdx] || {};
              selectedNames.current[listIdx].registry = selectedOptions.map(
                (o) => o.label,
              );
            },
            parseSelectOptions: (response) => parseSelectOptions(response.projects)
          },
        },
      ],
      [fetchRegistryRepo, selectedNames, listIdx],
    );

    const ciFields = useMemo(
      () => [
        {
          name: "ciId",
          colSpan: 12,
          type: FieldTypes.SELECT,
          placeholder: "Leave blank to select all",
          label: "Image Repository",
          help: "A scheduled job is run every 24 hours to fetch this data.",
          props: {
            mode: "multiple",
            api: fetchCICDRepo,
            onChange: (value, field, selectedOptions) => {
              selectedNames.current[listIdx] =
                selectedNames.current[listIdx] || {};
              selectedNames.current[listIdx].ci = selectedOptions.map(
                (o) => o.label,
              );
            },
            parseSelectOptions: (response) => parseSelectOptions(response.projects)
          },
        },
      ],
      [fetchCICDRepo, selectedNames, listIdx],
    );

    return (
      <div key={field.key}>
        {/* Image Type selector */}
        {generateFormFields(
          [
            {
              name: "imageType",
              label: "Image Type",
              type: FieldTypes.SELECT,
              colSpan: 12,
              options: IMAGE_TYPE_OPTIONS,
            },
            {
              name: "add",
              colSpan: 2,
              render: () => {
                return (
                  <Button
                    disabled={fieldsLength === 1}
                    type="dashed"
                    onClick={() => {
                      remove(field.name);
                    }}
                    className="m-t-l"
                  >
                    -
                  </Button>
                );
              },
            },
          ],
          24,
          field,
        )}

        {imgType === "deployed" &&
          <RenderDeployedList form={form} listIdx={listIdx} imageIdx={field.name} fetchRepos={fetchRepos} />}
        {imgType === "registry" &&
          generateFormFields(registryFields, 24, field)}
        {imgType === "ci" && generateFormFields(ciFields, 24, field)}
      </div>
    );
  },
);

const ImageBlockWithVersionMapping = React.memo(
  ({
    form,
    listIdx,
    field,
    remove,
    fetchRepos,
    selectedNames,
    fieldsLength,
  }) => {
    const values =
      form.getFieldsValue().configurations?.[listIdx]?.imageList || [];
    const imgType = values[field.name]?.imageType;
    // --- STATE & FETCHING for REGISTRY ---
    const [registryOptions, setRegistryOptions] = useState([]);
    const [isRegistryLoading, setIsRegistryLoading] = useState(true);
    const fetchRegistryRepo = useCallback(() => fetchRepos("registry"), [fetchRepos]);

    useEffect(() => {
      if (imgType === 'registry') {
        setIsRegistryLoading(true);
        const { promise, cancel } = fetchRegistryRepo();
        promise.then(data => setRegistryOptions(data.projects || [])).finally(() => setIsRegistryLoading(false));
        return cancel;
      }
    }, [imgType, fetchRegistryRepo]);

    // --- STATE & FETCHING for CI ---
    const [ciOptions, setCiOptions] = useState([]);
    const [isCiLoading, setIsCiLoading] = useState(true);
    const fetchCICDRepo = useCallback(() => fetchRepos("ci"), [fetchRepos]);

    useEffect(() => {
      if (imgType === 'ci') {
        setIsCiLoading(true);
        const { promise, cancel } = fetchCICDRepo();
        promise.then(data => setCiOptions(data.projects || [])).finally(() => setIsCiLoading(false));
        return cancel;
      }
    }, [imgType, fetchCICDRepo]);

    const onRepoChange = useCallback((value, field, fieldName, clearName) => {
        const imageList = form.getFieldsValue().configurations?.[listIdx]?.imageList || [];
        const newImageList = [...imageList];
        if (newImageList[field.name]) {
          newImageList[field.name] = {
            ...newImageList[field.name],
            [clearName]: [], // Reset to empty array
          };
          
          const configurations = form.getFieldValue("configurations");
          const newConfigs = [...configurations];
          newConfigs[listIdx] = {
            ...newConfigs[listIdx],
            imageList: newImageList,
          };
          form.setFieldsValue({ configurations: newConfigs });
        }
    }, [form, listIdx]);

    const registryFields = useMemo(
      () => [
        {
          name: "registryId",
          colSpan: 12,
          type: FieldTypes.SELECT,
          label: "Image Repository",
          placeholder: "Leave blank to select all",
          loading: isRegistryLoading,
          options: registryOptions,
          props: {
            onChange: (value, field, selectedOptions) => {
              // store the NAMES (you can pick .label or .name)
              selectedNames.current[listIdx] =
                selectedNames.current[listIdx] || {};
              selectedNames.current[listIdx].registry = selectedOptions ? [selectedOptions.label] : [];
              selectedNames.current[listIdx].registryNameMap = {
                ...(selectedNames.current[listIdx].registryNameMap || {}),
                [value]: selectedOptions ? selectedOptions.label : value
              };
              onRepoChange(value, field, "registryId", "versions");
            },
          },
        },
        {
          name: "versions",
          label: "Twistlock Image Version",
          colSpan: 12,
          type: FieldTypes.MULTI_SELECT,
          loading: isRegistryLoading,
          props: {
            allowClear: true,
            setExtraProps: (fieldConfig, listField) => {
              const imageRepoId = form.getFieldValue(["configurations", listIdx, "imageList", listField.name, "registryId"]);
              if (imageRepoId) {
                const selectedRepo = registryOptions.find(repo => repo.id === imageRepoId);
                return { options: parseVersionSelectOptions(selectedRepo?.otherProperties?.versions || []) };
              }
              return { options: [] };
            },
          }
        }
      ],
      [registryOptions, isRegistryLoading, onRepoChange, form, selectedNames, listIdx],
    );

    const ciFields = useMemo(
      () => [
        {
          name: "ciId",
          colSpan: 12,
          type: FieldTypes.SELECT,
          placeholder: "Leave blank to select all",
          label: "Image Repository",
          help: "A scheduled job is run every 24 hours to fetch this data.",
          options: ciOptions,
          loading: isCiLoading,
          props: {
            onChange: (value, field, selectedOptions) => {
              selectedNames.current[listIdx] =
                selectedNames.current[listIdx] || {};
              selectedNames.current[listIdx].ci = selectedOptions ? [selectedOptions.label] : [];
              selectedNames.current[listIdx].ciNameMap = {
                ...(selectedNames.current[listIdx].ciNameMap || {}),
                [value]: selectedOptions ? selectedOptions.label : value
              };
              onRepoChange(value, field, "ciId", "versions");
            },
          },
        },
        {
          name: "versions",
          label: "Twistlock Image Version",
          type: FieldTypes.SELECT,
          loading: isCiLoading,
          colSpan: 12,
          props: {
            mode: "multiple", // <-- Multiple versions
            allowClear: true,
            setExtraProps: (fieldConfig, listField) => {
              const imageRepoId = form.getFieldValue(["configurations", listIdx, "imageList", listField.name, "ciId"]);
              if (imageRepoId) {
                const selectedRepo = ciOptions.find(repo => repo.id === imageRepoId);
                return { options: parseVersionSelectOptions(selectedRepo?.otherProperties?.versions || []) };
              } else {
                return { options: [] };
              }
            },
          }
        }
      ],
      [ciOptions, selectedNames, listIdx, onRepoChange, isCiLoading, form],
    );

    return (
      <div key={field.key}>
        {/* Image Type selector */}
        {generateFormFields(
          [
            {
              name: "imageType",
              label: "Image Type",
              type: FieldTypes.SELECT,
              colSpan: 12,
              options: IMAGE_TYPE_OPTIONS,
            },
            {
              name: "add",
              colSpan: 2,
              render: () => {
                return (
                  <Button
                    disabled={fieldsLength === 1}
                    type="dashed"
                    onClick={() => {
                      remove(field.name);
                    }}
                    className="m-t-l"
                  >
                    -
                  </Button>
                );
              },
            },
          ],
          24,
          field,
        )}

        {imgType === "deployed" &&
          <RenderDeployedListWithVersionMapping 
            form={form} 
            listIdx={listIdx} 
            imageIdx={field.name} 
            fetchRepos={fetchRepos} 
            selectedNames={selectedNames} 
          />}
        {imgType === "registry" &&
          generateFormFields(registryFields, 24, field)}
        {imgType === "ci" && generateFormFields(ciFields, 24, field)}
      </div>
    );
  },
);

const RenderDeployedListWithVersionMapping = ({ form, listIdx, imageIdx, fetchRepos, selectedNames }) => {
  const [allImageOptions, setAllImageOptions] = useState([]);
  // Store loading state
  const [isLoading, setIsLoading] = useState(true);

  const clusterChange = useCallback(
    (type, value, field) => {
      const deployIdx = field.name; // index within deployedList

      // grab the full configurations array
      const configurations = form.getFieldValue("configurations") || [];

      // shallow-clone top level
      const newConfigs = configurations.map((row, r) =>
        r === listIdx
          ? {
              ...row,
              imageList: row.imageList.map((img, i) =>
                i === imageIdx
                  ? {
                      ...img,
                      // only reset that one deployedList entry’s imageId
                      deployedList: img.deployedList.map((dl, d) =>
                        d === deployIdx ? { ...dl, imageId: [], versions: [] } : dl,
                      ),
                    }
                  : img,
              ),
            }
          : row,
      );

      form.setFieldsValue({ configurations: newConfigs });
    },
    [form, imageIdx, listIdx],
  );

  const memoizedFetch = useCallback(() => {
    return fetchRepos("image"); 
  }, [fetchRepos]);

  // Fetch data on component mount
  useEffect(() => {
    setIsLoading(true);
    const { promise, cancel } = memoizedFetch();
    promise.then((data) => {
      setAllImageOptions(data.projects || []);
    }).finally(() => {
      setIsLoading(false);
    });
    return cancel;
  }, [memoizedFetch]);

  const imageChange = useCallback((value, field) => {
      const deployIdx = field.name;
      const configurations = form.getFieldValue("configurations") || [];
      const newConfigs = configurations.map((row, r) =>
        r === listIdx
          ? {
              ...row,
              imageList: row.imageList.map((img, i) =>
                i === imageIdx
                  ? {
                      ...img,
                      deployedList: img.deployedList.map((dl, d) =>
                        d === deployIdx 
                        ? { ...dl, versions: [] }
                        : dl,
                      ),
                    }
                  : img,
              ),
            }
          : row,
      );
      form.setFieldsValue({ configurations: newConfigs });
  }, [form, imageIdx, listIdx]);

  const getDeployedFields = useCallback(
    (add, field) => [
      {
        label: "Cluster",
        name: "clusterName",
        colSpan: 10,
        type: FieldTypes.SELECT,
        layout: "vertical",
        placeholder: "Leave blank to select all",
        help: "Specify a cluster to pull vulnerabilities for deployed images.",
        loading: isLoading,
        props: {
          onChange: (value, field) => clusterChange("image", value, field),
          options: parseClusterSelectOption({projects: allImageOptions}),
        },
      },
      {
        label: "Twistlock Image",
        name: "imageId",
        colSpan: 10,
        type: FieldTypes.SELECT,
        placeholder: "Leave blank to select all",
        props: {
          onChange: imageChange,
          loading: isLoading,
          setExtraProps: (field, listField) => {
            const cluster = form.getFieldValue(["configurations", listIdx, "imageList", imageIdx, "deployedList", listField.name, "clusterName"]);
            
            let filteredOptions = allImageOptions;

            if (isNull(cluster)) {
              filteredOptions = filter(allImageOptions, (data) => isEmpty(data.otherProperties.clusters));
              } else if (isUndefined(cluster)) {
              filteredOptions = allImageOptions;
              } else {
              filteredOptions = filter(allImageOptions, (data) => data.otherProperties.clusters?.includes(cluster));
            }

            return { options: filteredOptions };
          },
        },
      },
      {
          label: "Twistlock Version",
          name: "versions",
          colSpan: 10,
          type: FieldTypes.SELECT,
          placeholder: "Leave blank to select all",
          props: {
          mode: "multiple",
          setExtraProps: (fieldConfig, listField) => {
            const cluster = form.getFieldValue(["configurations", listIdx, "imageList", imageIdx, "deployedList", listField.name, "clusterName"]);
              const imageId = form.getFieldValue(["configurations", listIdx, "imageList", imageIdx, "deployedList", listField.name, "imageId"]);

              let clusterOptions = allImageOptions;
              if (isNull(cluster)) {
                clusterOptions = filter(allImageOptions, (data) => isEmpty(data.otherProperties.clusters));
              } else if (isUndefined(cluster)) {
                clusterOptions = allImageOptions;
              } else {
                clusterOptions = filter(allImageOptions, (data) => data.otherProperties.clusters?.includes(cluster));
              }

              if (imageId) {
                const selectedRepo = clusterOptions.find(repo => repo.id === imageId);
                return { options: parseVersionSelectOptions(selectedRepo?.otherProperties?.versions || []) };
              }
              return { options: [] };
          },
        },
      },
      {
        name: "add",
        colSpan: 2,
        render: ({ remove, name, fields }) => {
          return (
            <div className="d-flex" style={{ marginTop: 28 }}>
              <Button
                size="small"
                type="dashed"
                onClick={() =>
                  add({ clusterName: undefined, imageId: undefined, versions: [] })
                }
              >
                +
              </Button>
              <Button
                size="small"
                onClick={() => remove(field.name)}
                className="m-l-xs"
                disabled={fields.length === 1}
              >
                -
              </Button>
            </div>
          );
        },
      },
    ],
    [clusterChange, form, imageIdx, listIdx, isLoading, allImageOptions, imageChange, selectedNames],
  );
  return (
    <Form.List name={[imageIdx, "deployedList"]}>
      {(fields, { add, remove }) => (
        <div>
          {fields.map((field) =>
            generateFormFields(
              getDeployedFields(add, field),
              10,
              { ...field, fields, add, remove },
              field.name,
            ),
          )}
        </div>
      )}
    </Form.List>
  );
};


const RenderDeployedList = ({ form, listIdx, imageIdx, fetchRepos }) => {
  // --- STATE & FETCHING ---
  const [allImageOptions, setAllImageOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchImageOptions = useCallback(() => {
    return fetchRepos("image");
  }, [fetchRepos]);

  useEffect(() => {
    setIsLoading(true);
    const { promise, cancel } = fetchImageOptions();
    promise.then((data) => {
      setAllImageOptions(data.projects || []);
    }).finally(() => {
      setIsLoading(false);
    });
    return cancel;
  }, [fetchImageOptions]);

  const clusterChange = useCallback(
    (type, value, field) => {
      const deployIdx = field.name; // index within deployedList

      // grab the full configurations array
      const configurations = form.getFieldValue("configurations") || [];

      // shallow-clone top level
      const newConfigs = configurations.map((row, r) =>
        r === listIdx
          ? {
              ...row,
              imageList: row.imageList.map((img, i) =>
                i === imageIdx
                  ? {
                      ...img,
                      // only reset that one deployedList entry’s imageId
                      deployedList: img.deployedList.map((dl, d) =>
                        d === deployIdx ? { ...dl, imageId: [] } : dl,
                      ),
                    }
                  : img,
              ),
            }
          : row,
      );

      form.setFieldsValue({ configurations: newConfigs });
    },
    [form, imageIdx, listIdx],
  );

  const getDeployedFields = useCallback(
    (add, field) => [
      {
        label: "Cluster",
        name: "clusterName",
        colSpan: 10,
        type: FieldTypes.SELECT,
        layout: "vertical",
        placeholder: "Leave blank to select all",
        help: "Specify a cluster to pull vulnerabilities for deployed images.",
        options: parseClusterSelectOption({projects: allImageOptions}),
        loading: isLoading,
        props: {
          onChange: (value, field) => clusterChange("image", value, field),
          parseSelectOptions: parseClusterSelectOption,
        },
      },
      {
        label: "Twistlock Image",
        name: "imageId",
        colSpan: 10,
        type: FieldTypes.SELECT,
        placeholder: "Leave blank to select all",
        loading: isLoading,
        props: {
          mode: "multiple",
          setExtraProps: (fieldConfig, listField)=> {
            const cluster = form.getFieldValue(["configurations", listIdx, "imageList", imageIdx, "deployedList", listField.name, "clusterName"]);
            let filteredOptions = allImageOptions;
            if (isNull(cluster)) {
              filteredOptions = filter(allImageOptions, (data) => isEmpty(data.otherProperties.clusters));
            } else if (isUndefined(cluster)) {
              filteredOptions = allImageOptions;
            } else {
              filteredOptions = filter(allImageOptions, (data) => data.otherProperties.clusters?.includes(cluster));
            }
            return { options: filteredOptions };
          },
        },
      },
      {
        name: "add",
        colSpan: 2,
        render: ({ remove, name, fields }) => {
          return (
            <div className="d-flex" style={{ marginTop: 28 }}>
              <Button
                size="small"
                type="dashed"
                onClick={() =>
                  add({ clusterName: undefined, imageId: undefined })
                }
              >
                +
              </Button>
              <Button
                size="small"
                onClick={() => remove(field.name)}
                className="m-l-xs"
                disabled={fields.length === 1}
              >
                -
              </Button>
            </div>
          );
        },
      },
    ],
    [clusterChange, allImageOptions, isLoading, form, imageIdx, listIdx],
  );
  return (
    <Form.List name={[imageIdx, "deployedList"]}>
      {(fields, { add, remove }) => (
        <div>
          {fields.map((field) =>
            generateFormFields(
              getDeployedFields(add, field),
              10,
              { ...field, fields, add, remove },
              field.name,
            ),
          )}
        </div>
      )}
    </Form.List>
  );
};

const HostRenderer = ({
  form,
  listField,
  selectedNames,
  fetchRepos,
}) => {
  const appendAll = useCallback(
    (list = []) => [
      { label: "Not mapped with cluster", value: NOT_MAPPED },
      ...list,
    ],
    [],
  );

  const hostClusterChange = useCallback(
    (value, field) => {
      if(field){
        const rowIdx = field.name;
        const configurations = form.getFieldValue("configurations") || [];
        const newConfigs = configurations.map((row, r) =>
          r === rowIdx ? { ...row, hostClusterName: value, hostId: [] } : row,
        );
        form.setFieldsValue({ configurations: newConfigs });
      }
    },
    [form],
  );

  const fetchHostRepo = useCallback((params) => {
    return fetchRepos("host", params)
  }, [fetchRepos]);

  const hostFields = useMemo(
    () => [
      {
        label: "Host Cluster",
        name: "hostClusterName",
        colSpan: 10,
        type: FieldTypes.SELECT,
        placeholder: "Leave blank to select all",
        layout: "vertical",
        help: "If cluster name is specified, then vulnerabilities  related to image  deployed within cluster will be pulled.",
        props: {
          onChange: (value, option, field) => {
            // store cluster label
            selectedNames.current[listField.name] =
              selectedNames.current[listField.name] || {};
            selectedNames.current[listField.name].hostCluster = option.label;
            hostClusterChange(value, field);
          },
          api: fetchHostRepo,
          parseSelectOptions: parseClusterSelectOption,
          optionsMutator: appendAll,
          allowClear: true
        },
      },
      {
        name: "hostId",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: "Host name",
        props: {
          mode: "multiple",
          api: fetchHostRepo,
          onChange: (value, field, selectedOptions) => {
            // store host labels
            selectedNames.current[listField.name] =
              selectedNames.current[listField.name] || {};
            selectedNames.current[listField.name] = selectedOptions.map(
              (o) => o.label,
            );
          },
          getFilterOptions: (fieldKey, options) => {
            const fieldConfiguration =
              form.getFieldValue("configurations")[fieldKey];
            const cluster = fieldConfiguration?.hostClusterName;
            if (cluster === NOT_MAPPED) {
              return options?.filter((host) =>
                isEmpty(host.otherProperties.clusters),
              );
            } else if (isUndefined(cluster)) {
              return options;
            } else {
              return options?.filter(
                (host) => host.otherProperties.clusters === cluster,
              );
            }
          },
          setExtraProps: (field, listField) => {
            const { name } = listField;
            const config = form.getFieldValue("configurations")[name];
            return {
              disabled: config?.allHosts,
            };
          },
        },
      },
      {
        label: "All",
        name: "allHosts",
        colSpan: 2,
        type: FieldTypes.BOOLEAN,
        props: {
          onChange: (e) => {
            const { name } = e.target.listField;
            const configurations = form.getFieldValue("configurations");
            const updatedConfiguration = [...configurations];
            updatedConfiguration[name] = {
              ...updatedConfiguration[name],
              hostId: [],
            };
            form.setFieldsValue({ configurations: updatedConfiguration });
          },
        },
      },
    ],
    [
      hostClusterChange,
      appendAll,
      form,
      listField.name,
      selectedNames,
      fetchHostRepo,
    ],
  );

  return (
    <>
      <Alert
        type="warning"
        className="m-b-sm"
        message="This config will take time to load."
      />
      {generateFormFields(hostFields, 24, listField)}
    </>
  );
};

const AppSecRenderer = ({ fetchRepos, listField, selectedNames }) => {
  const fetchCodeRepo = useCallback((params) => {
    return fetchRepos("code", params)
  }, [fetchRepos]);

  const codeFields = useMemo(
    () => [
      {
        name: "codeRepoId",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        placeholder: "Leave blank to select all",
        label: "Project Name",
        props: {
          mode: "multiple",
          api: fetchCodeRepo,
          onChange: (value, field, selectedOptions) => {
            selectedNames.current[listField.name] =
              selectedNames.current[listField.name] || {};
            selectedNames.current[listField.name].codeRepo =
              selectedOptions.map((o) => o.label);
          },
          allowClear: true
        },
      },
    ],
    [fetchCodeRepo, selectedNames, listField.name],
  );

  return generateFormFields(codeFields, 24, listField);
};

const NOT_MAPPED = "NOT_MAPPED";

const IMAGE_RENDERER = ({ form, listField, fetchRepos, selectedNames }) => {
  const featureFlags = useFeatureFlag();
  const isVersionMappingEnabled = featureFlags.PRISMA_COMPUTE_ENABLE_IMAGE_VERSION_MAPPING;

  return (
  <Form.List name={[listField.name, "imageList"]}>
    {(fields, { add, remove }) => (
      <>
        {fields.map((field) => isVersionMappingEnabled 
          ? 
          <ImageBlockWithVersionMapping 
          key={field.key}
          form={form}
          listIdx={listField.name}
          field={field}
          remove={remove}
          fetchRepos={fetchRepos}
          selectedNames={selectedNames}
          fieldsLength={fields.length}
          /> 
          :
          <ImageBlock
            key={field.key}
            form={form}
            listIdx={listField.name}
            field={field}
            remove={remove}
            fetchRepos={fetchRepos}
            selectedNames={selectedNames}
            fieldsLength={fields.length}
          />
        )}

        <Button
          size="small"
          type="dashed"
          className="m-t-xs m-b-xs"
          onClick={() =>
            add({
              imageType: "deployed",
              deployedList: [
                { clusterName: undefined, imageId: undefined, versions: featureFlags.PRISMA_COMPUTE_ENABLE_IMAGE_VERSION_MAPPING ? [] : undefined },
              ],
            })
          }
        >
          + Add Images
        </Button>
      </>
    )}
  </Form.List>
)};

const ApiSecurityRenderer = ({ listField }) => {
  const apiFields =  [{
    name: "isApiSecurityConfig",
    colSpan: 24,
    type: FieldTypes.BOOLEAN,
    help: "Ingests API Security Findings reported across workloads",
    label: <>Ingest API Security Findings from Prisma Cloud Compute</>,
    inline: true,
  }]

 return generateFormFields(apiFields, 24, listField);
}